import ContributionService from "@/services/FinEaseUser/APIs/ContributionService";
import {
  CreditCard,
  Info,
  CircleAlert,
  Gift,
  CircleCheckBig,
  Receipt,
  Building2,
  Percent,
  Wallet,
  Check,
  Calendar,
  Download,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { getTenantId } from "@/lib/storeManager";
import { ITier1List } from "@/utils/types/Contributions";
import { BaseTable } from "@/common/components/BaseTable";
import { ProColumns } from "@ant-design/pro-components";
import Select, { components } from "react-select";
import { useMemo, useState } from "react";
import { CommonModal } from "@/common/components/ui";
import { Registration } from "@/utils/types/NPS/Stepper/GetRegistrationDetils";
import ContributionsPayModal from "@/projects/NPS/Stepper/common/ContributionsPayModal";

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: "40px",
    borderRadius: "8px",
    borderColor: state.isDisabled ? "var(--border)" : "var(--border)",
    backgroundColor: state.isDisabled ? "var(--muted)" : "var(--background)",
    opacity: state.isDisabled ? 0.6 : 1,
    cursor: state.isDisabled ? "not-allowed" : "pointer",
    boxShadow: "none",
    "&:hover": {
      borderColor: state.isDisabled ? "var(--border)" : "var(--border)",
    },
  }),

  singleValue: (base: any, state: any) => ({
    ...base,
    color: state.isDisabled ? "var(--muted-foreground)" : "var(--foreground)",
  }),

  placeholder: (base: any, state: any) => ({
    ...base,
    color: state.isDisabled ? "var(--muted-foreground)" : base.color,
  }),

  menu: (base: any) => ({
    ...base,
    zIndex: 9999,
    borderRadius: "8px",
    padding: "4px",
  }),

  option: () => ({}), // custom Option handles it

  indicatorSeparator: () => ({ display: "none" }),

  dropdownIndicator: (base: any, state: any) => ({
    ...base,
    color: state.isDisabled ? "var(--muted-foreground)" : base.color,
  }),
};

const TierOption = (props: any) => {
  const { isSelected, isDisabled, data } = props;

  return (
    <components.Option {...props}>
      <div
        className={`flex items-center justify-between rounded-md px-3 py-2 ${isSelected ? "bg-primary text-white" : "bg-white"} ${isDisabled ? "cursor-not-allowed opacity-50" : ""} `}
      >
        <div className="flex items-center gap-2">
          {isSelected && <Check className="h-4 w-4" />}
          <span className="font-medium">{data.label}</span>

          {data.badge && (
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                isSelected
                  ? "bg-white/20 text-white"
                  : "border text-muted-foreground"
              } `}
            >
              {data.badge}
            </span>
          )}
        </div>
      </div>
    </components.Option>
  );
};

const TierSingleValue = (props: any) => {
  const { data } = props;

  return (
    <components.SingleValue {...props}>
      <div className="flex items-center gap-2">
        <span>{data.label}</span>
        {data.badge && (
          <span className="rounded-full border px-2 py-0.5 text-xs font-semibold">
            {data.badge}
          </span>
        )}
      </div>
    </components.SingleValue>
  );
};

const ContributionsTab = ({
  registrationDetails,
}: {
  registrationDetails: Registration | null | undefined;
}) => {
  const tenantId = getTenantId() ?? "";
  const [amountInput, setAmountInput] = useState<any>(1000);
  const [amountError, setAmountError] = useState<string>("");
  const [showPayModal, setShowPayModal] = useState(false);

  const handleClosePayModal = () => {
    setShowPayModal(false);
  };

  const handleOpenPayModal = () => {
    setShowPayModal(true);
  };

  // --- Calculations ---
  const popCharge = useMemo(() => {
    const calculated = (amountInput ?? 0) * 0.002;
    return Math.max(calculated, 0);
  }, [amountInput]);

  const gstCharge = useMemo(() => {
    return popCharge * 0.18; // 18% GST
  }, [popCharge]);

  const totalCharges = useMemo(() => {
    return popCharge + gstCharge;
  }, [popCharge, gstCharge]);

  const totalPayable = useMemo(() => {
    return (amountInput ?? 0) + totalCharges;
  }, [amountInput, totalCharges]);

  const {
    data,
    isLoading,
    refetch: refetchContributions,
  } = useQuery({
    queryKey: ["contributions", tenantId],
    queryFn: () => ContributionService.listContributions(),
    enabled: !!tenantId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 60_000,
  });

  const tierOptions = [
    {
      value: "tier1",
      label: "Tier 1 (Pension Account)",
      badge: "Tax Benefits",
    },
    {
      value: "tier2",
      label: "Tier 2 (Investment Account)",
      badge: "Flexible",
    },
  ];

  const columns: ProColumns<ITier1List>[] = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
    },
    {
      title: "Tier Type",
      dataIndex: "tierType",
      key: "tierType",
    },
    {
      title: "Contribution Amount",
      dataIndex: "contriAmt",
      key: "contriAmt",
    },
    {
      title: "CR/DB Type",
      dataIndex: "cr_db_Type",
      key: "cr_db_Type",
    },
  ];

  return (
    <>
      <div
        role="tabpanel"
        className="mt-6 space-y-6 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {/* GRID MAIN */}
        <div className="rounded-2xl border border-border/60 bg-card shadow-sm transition-shadow hover:shadow-md">
          {/* ===== Header ===== */}
          <div className="space-y-2 border-b p-6">
            <div className="flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-lg font-bold text-foreground">
                <CreditCard className="h-5 w-5 text-primary" />
                Make Contribution
              </h3>

              <span className="rounded-full border px-2.5 py-0.5 text-xs font-semibold">
                As per PFRDA Norms
              </span>
            </div>

            <p className="text-sm text-muted-foreground">
              Choose your contribution type and fund allocation as per PFRDA
              guidelines
            </p>
          </div>

          {/* ===== Body ===== */}
          <div className="grid grid-cols-1 lg:grid-cols-2">
            {/* ===== Left Form ===== */}
            <div className="space-y-5 border-r p-6">
              {/* Tier */}
              <div>
                <label className="text-sm font-medium">Select Tier</label>

                <Select
                  className="mt-2"
                  defaultValue={tierOptions[0]}
                  options={tierOptions}
                  styles={selectStyles}
                  isDisabled
                  components={{
                    Option: TierOption,
                    SingleValue: TierSingleValue,
                  }}
                  getOptionLabel={e => e.label}
                  getOptionValue={e => e.value}
                />

                <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                  <Info className="h-3 w-3" />
                  Tier 1 is mandatory. Min ₹500/contribution, ₹1,000/year.
                </p>
              </div>

              {/* Contribution Type */}
              <div>
                <label className="text-sm font-medium">Contribution Type</label>
                <Select
                  className="mt-2"
                  defaultValue={{
                    value: "one-time",
                    label: "One-time Contribution",
                  }}
                  isDisabled
                  styles={selectStyles}
                  options={[
                    { value: "one-time", label: "One-time Contribution" },
                    { value: "recurring", label: "Recurring Contribution" },
                  ]}
                  components={{
                    Option: TierOption,
                    SingleValue: TierSingleValue,
                  }}
                  getOptionLabel={e => e.label}
                  getOptionValue={e => e.value}
                />
              </div>

              {/* Amount */}
              <div>
                <label className="text-sm font-medium">Amount (₹)</label>

                <input
                  type="text"
                  value={amountInput}
                  onChange={e => {
                    const value = e.target.value;
                    if (!/^₹?[0-9]*\.?[0-9]*$/.test(value)) {
                      return;
                    }

                    setAmountInput(value);
                    const numericValue = parseFloat(
                      value.replace(/[^0-9.]/g, "")
                    );
                    if (!value.trim() || isNaN(numericValue)) {
                      setAmountError("Minimum contribution amount is ₹500");
                      return;
                    }
                    if (numericValue < 500) {
                      setAmountError(
                        "Minimum contribution amount must be ₹500 or more"
                      );
                    } else {
                      setAmountError("");
                    }
                  }}
                  className={`mt-1 w-full rounded-md border px-3 py-2.5 font-sans text-[16px] font-semibold text-[#2F363F] placeholder-[#BECAD0] outline-none focus:border-[#66A0CD] ${amountError ? "border-red-500" : "border-[#DFE7EC]"}`}
                  placeholder="₹1000"
                />

                {/* INFO STACK */}
                <div className="mt-3 space-y-3">
                  {/* PFRDA Rules */}
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <div className="flex gap-2">
                      <CircleAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600" />

                      <div className="space-y-1 text-xs text-amber-700">
                        <p className="font-medium">PFRDA Contribution Rules</p>
                        <ul className="list-inside list-disc space-y-0.5">
                          <li>Minimum ₹500 per contribution (Tier 1)</li>
                          <li>
                            Minimum ₹1,000 annually to keep account active
                          </li>
                          <li>No maximum limit on contribution</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Tax Benefits */}
                  <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                    <div className="flex items-start gap-2">
                      <Gift className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600" />

                      <div>
                        <p className="text-sm font-semibold text-green-800">
                          Tax Benefits Available
                        </p>

                        <ul className="mt-1 space-y-1 text-xs text-green-700">
                          <li className="flex gap-2">
                            <CircleCheckBig className="h-3 w-3 text-green-600" />
                            <span>
                              <strong>80CCD(1):</strong> Up to ₹1.5L (within
                              80C)
                            </span>
                          </li>

                          <li className="flex gap-2">
                            <CircleCheckBig className="h-3 w-3 text-green-600" />
                            <span>
                              <strong>80CCD(1B):</strong> Extra ₹50,000
                            </span>
                          </li>

                          <li className="flex gap-2">
                            <CircleCheckBig className="h-3 w-3 text-green-600" />
                            <span>
                              <strong>80CCD(2):</strong> Employer up to 10% (14%
                              Govt)
                            </span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ===== Right Summary ===== */}
            <div className="space-y-4 bg-gradient-to-br from-primary/5 to-accent/5 p-6">
              <div className="flex items-center justify-between">
                <p className="flex items-center gap-2 text-sm font-semibold text-secondary">
                  <Receipt className="h-4 w-4 text-primary" />
                  Contribution Breakdown
                </p>

                <span className="rounded-full border px-2 py-0.5 text-xs font-semibold">
                  eNPS Platform
                </span>
              </div>

              <div>
                <p className="text-xs text-muted-foreground">
                  Charges applicable as per PFRDA &amp; Protean guidelines
                </p>
              </div>

              {/* Amount */}
              <div className="rounded-xl border bg-white p-4">
                <p className="text-sm text-muted-foreground">
                  NPS Investment Amount
                </p>
                <p className="mt-1 text-3xl font-bold text-green-600">
                  ₹
                  {Number.isFinite(amountInput ?? 0)
                    ? (amountInput ?? 0).toLocaleString("en-IN")
                    : "0"}
                </p>
                <p className="mt-1 text-xs text-green-600">
                  Full amount goes to NPS account
                </p>
              </div>

              {/* Charges */}
              <div className="space-y-2">
                {/* Title */}
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  PFRDA Charges (w.e.f. 31.01.2025)
                </p>

                {/* POP Charges */}
                <div className="flex items-center justify-between rounded-lg border bg-white p-3">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-blue-600" />

                    <div>
                      <span className="text-sm text-secondary">
                        POP Charges
                      </span>
                      <p className="text-xs text-muted-foreground">
                        0.20% (Min ₹15, Max ₹10,000)
                      </p>
                    </div>
                  </div>

                  <span className="font-semibold text-blue-600">
                    + ₹{popCharge.toFixed(2)}
                  </span>
                </div>

                {/* GST Charges */}
                <div className="flex items-center justify-between rounded-lg border bg-white p-3">
                  <div className="flex items-center gap-2">
                    <Percent className="h-4 w-4 text-orange-500" />

                    <div>
                      <span className="text-sm text-secondary">
                        GST on POP Charges
                      </span>
                      <p className="text-xs text-muted-foreground">
                        18% on POP charges
                      </p>
                    </div>
                  </div>

                  <span className="font-semibold text-orange-500">
                    + ₹{gstCharge.toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="space-y-2 border-t pt-3">
                <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 p-3">
                  <span className="text-sm font-medium text-blue-800">
                    Total Charges
                  </span>
                  <span className="font-bold text-blue-700">
                    + ₹{totalCharges.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between rounded-lg border border-primary/20 bg-primary/10 p-3">
                  <div className="flex gap-2">
                    <Wallet className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium text-primary">
                      Total Payable
                    </span>
                  </div>
                  <span className="text-lg font-bold text-primary">
                    ₹{totalPayable.toFixed(2)}
                  </span>
                </div>
                <p className="text-center text-xs text-muted-foreground">
                  Payment gateway charges will be added separately at checkout
                </p>
              </div>

              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <div className="flex items-start gap-2">
                  <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />

                  <p className="text-xs text-blue-700">
                    Charges are as per PFRDA circular dated 31.03.2023. POP
                    charges capped at 0.10% of contribution. PFM charges are
                    deducted from NAV daily.
                  </p>
                </div>
              </div>

              <button
                onClick={handleOpenPayModal}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary font-semibold text-white hover:bg-primary/90"
              >
                <CreditCard className="h-4 w-4" />
                Pay ₹{totalPayable.toFixed(2)}
              </button>

              <p className="text-center text-xs text-muted-foreground">
                Powered by Protean eGov Technologies Limited (formerly NSDL
                eGov) - Central Recordkeeping Agency appointed by PFRDA
              </p>
            </div>
          </div>
        </div>

        {/* ---------------------- CONTRIBUTION HISTORY TABLE ---------------------- */}
        <div className="rounded-2xl border border-border/60 bg-card shadow-sm transition-shadow hover:shadow-md">
          <div className="flex items-center justify-between p-6 pb-3">
            <h3 className="flex items-center gap-2 text-base font-bold tracking-tight">
              <Calendar className="h-5 w-5 text-primary" />
              Contribution History
            </h3>

            <button className="flex h-9 items-center gap-2 rounded-lg border border-border px-4 text-xs hover:bg-muted/50">
              <Download className="h-4 w-4" />
              Export
            </button>
          </div>

          <div className="overflow-x-auto p-4">
            <BaseTable<ITier1List>
              loading={isLoading}
              columns={columns}
              dataSource={
                (data?.lastTransaction?.tier1List ?? []) as ITier1List[]
              }
              total={10}
              onPageChange={() => {}}
              rowKey="id"
              page={1}
              pageSize={10}
              headerTitle=""
              toolBarRender={undefined}
              request={() => refetchContributions()}
            />
          </div>
        </div>
      </div>
      <CommonModal
        isOpen={showPayModal}
        onClose={handleClosePayModal}
        width="w-full sm:w-[900px]"
        height="h-auto"
        padding="p-2 sm:p-6"
      >
        <ContributionsPayModal
          amount={amountInput}
          registrationDetails={registrationDetails}
        />
      </CommonModal>
    </>
  );
};

export default ContributionsTab;
